import React, { useState, useEffect, Fragment } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { apiService } from '../services/api';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: clamp(1rem, 3vw, 1.25rem);
`;

const PageTitle = styled.h1`
  color: ${props => props.$isDarkMode ? '#ffffff' : '#333'};
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Section = styled.div`
  background: ${props => props.$isDarkMode ? '#2d2d2d' : 'white'};
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: ${props => props.$isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)'};
  border: ${props => props.$isDarkMode ? '1px solid #404040' : 'none'};
`;

const SectionTitle = styled.h2`
  color: ${props => props.$isDarkMode ? '#ffffff' : '#333'};
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: ${props => props.$isDarkMode ? '2px solid #404040' : '2px solid #f0f0f0'};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(200px, 100%), 1fr));
  gap: 16px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-weight: 600;
  color: ${props => props.$isDarkMode ? '#ffffff' : '#333'};
  font-size: 14px;
`;

const Select = styled.select`
  padding: 10px;
  border: ${props => props.$isDarkMode ? '2px solid #404040' : '2px solid #e1e5e9'};
  border-radius: 6px;
  font-size: 14px;
  background: ${props => props.$isDarkMode ? '#1e1e1e' : 'white'};
  color: ${props => props.$isDarkMode ? '#ffffff' : '#333'};
  
  &:focus {
    outline: none;
    border-color: ${props => props.$isDarkMode ? '#4dabf7' : '#007bff'};
  }
`;

const Input = styled.input`
  padding: 10px;
  border: ${props => props.$isDarkMode ? '2px solid #404040' : '2px solid #e1e5e9'};
  border-radius: 6px;
  font-size: 14px;
  background: ${props => props.$isDarkMode ? '#1e1e1e' : 'white'};
  color: ${props => props.$isDarkMode ? '#ffffff' : '#333'};
  
  &:focus {
    outline: none;
    border-color: ${props => props.$isDarkMode ? '#4dabf7' : '#007bff'};
  }
`;

const Button = styled.button`
  background: ${props => {
    if (props.variant === 'secondary') return props.$isDarkMode ? '#555' : '#6c757d';
    if (props.variant === 'success') return '#28a745';
    if (props.variant === 'warning') return '#ffc107';
    return props.$isDarkMode ? '#4dabf7' : '#007bff';
  }};
  color: ${props => props.variant === 'warning' ? '#333' : 'white'};
  border: none;
  padding: 10px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    transform: translateY(-1px);
  }
  
  &:disabled {
    background: ${props => props.$isDarkMode ? '#333' : '#6c757d'};
    cursor: not-allowed;
    transform: none;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  
  @media (max-width: 480px) {
    flex-direction: column;
    
    > button {
      width: 100%;
    }
  }
`;

const TransactionTable = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin: 0 -1rem;
  padding: 0 1rem;
  
  @media (max-width: 768px) {
    margin: 0 -0.5rem;
    padding: 0 0.5rem;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 16px;
  min-width: 700px;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const Th = styled.th`
  background: ${props => props.$isDarkMode ? '#1e1e1e' : '#f8f9fa'};
  padding: 12px;
  text-align: left;
  font-weight: 600;
  color: ${props => props.$isDarkMode ? '#ffffff' : '#333'};
  border-bottom: ${props => props.$isDarkMode ? '2px solid #404040' : '2px solid #dee2e6'};
  white-space: nowrap;
  
  &:nth-child(1) { width: 90px; min-width: 90px; } /* Date */
  &:nth-child(2) { width: 130px; min-width: 130px; } /* Account */
  &:nth-child(3) { width: 80px; min-width: 80px; } /* Type */
  &:nth-child(4) { width: 100px; min-width: 100px; } /* Amount */
  &:nth-child(5) { width: 90px; min-width: 90px; } /* Status */
  &:nth-child(6) { width: auto; min-width: 120px; } /* Description */
  &:nth-child(7) { width: 110px; min-width: 110px; } /* Reference */
`;

const Td = styled.td`
  padding: 12px;
  border-bottom: ${props => props.$isDarkMode ? '1px solid #404040' : '1px solid #dee2e6'};
  color: ${props => props.$isDarkMode ? '#ffffff' : '#333'};
  
  &.amount {
    font-weight: 600;
    color: ${props => props.$isDarkMode ? '#ffffff' : '#333'};
    text-align: right;
  }
  
  &.status {
    font-weight: 600;
  }
  
  &:nth-child(1) { width: 90px; } /* Date */
  &:nth-child(2) { width: 130px; word-break: break-word; } /* Account */
  &:nth-child(3) { width: 80px; text-align: center; } /* Type */
  &:nth-child(4) { width: 100px; text-align: right; font-weight: 600; } /* Amount */
  &:nth-child(5) { width: 90px; text-align: center; } /* Status */
  &:nth-child(6) { width: auto; } /* Description */
  &:nth-child(7) { width: 110px; } /* Reference */
`;

const StatusBadge = styled.span`
  background: ${props => 
    props.status === 'COMPLETED' ? '#28a745' : 
    props.status === 'PENDING' ? '#ffc107' : '#dc3545'
  };
  color: ${props => props.status === 'PENDING' ? '#333' : 'white'};
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
`;

const TypeBadge = styled.span`
  background: ${props => 
    props.type === 'CREDIT' ? '#28a745' : 
    props.type === 'DEBIT' ? '#dc3545' : 
    props.type === 'INTERNAL_TRANSFER' ? '#17a2b8' :
    props.type === 'THIRD_PARTY_TRANSFER' ? '#6c757d' :
    props.type === 'EXTERNAL_TRANSFER_OUT' ? '#fd7e14' :
    props.type === 'EXTERNAL_TRANSFER_IN' ? '#28a745' : '#6c757d'
  };
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
`;

const LoadingSpinner = styled.div`
  border: 3px solid ${props => props.$isDarkMode ? '#404040' : '#f3f3f3'};
  border-top: 3px solid ${props => props.$isDarkMode ? '#4dabf7' : '#007bff'};
  border-radius: 50%;
  width: 20px;
  height: 20px;
  animation: spin 1s linear infinite;
  display: inline-block;
  margin-right: 8px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background: ${props => props.$isDarkMode ? '#3a1e1e' : '#f8d7da'};
  color: ${props => props.$isDarkMode ? '#ff6b6b' : '#721c24'};
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 16px;
  border: ${props => props.$isDarkMode ? '1px solid #ff6b6b' : '1px solid #f5c6cb'};
`;

const EmptyState = styled.div`
  text-align: center;
  color: ${props => props.$isDarkMode ? '#b3b3b3' : '#666'};
  padding: 40px 0;
  font-size: 16px;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div`
  background: ${props => 
    props.$isDarkMode 
      ? (props.color ? props.darkColor || '#2d2d2d' : '#2d2d2d')
      : (props.color || '#f8f9fa')
  };
  border-radius: 8px;
  padding: 16px;
  text-align: center;
  border: ${props => props.$isDarkMode ? '1px solid #404040' : 'none'};
`;

const SummaryTitle = styled.div`
  font-size: 14px;
  color: ${props => props.$isDarkMode ? '#b3b3b3' : '#666'};
  margin-bottom: 8px;
`;

const SummaryValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${props => props.$isDarkMode ? '#ffffff' : '#333'};
`;

function TransactionHistoryPage() {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const [accounts, setAccounts] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [allTransactionsForSummary, setAllTransactionsForSummary] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalTransactions: 0,
    limit: 20
  });

  const [filters, setFilters] = useState({
    accountId: '',
    status: '',
    type: '',
    feeFilter: '', // New filter for fees
    dateFrom: '',
    dateTo: '',
    searchQuery: '',
    page: 1,
    limit: 10  // ✅ Установили 10 записей на страницу
  });

  useEffect(() => {
    loadAccounts();
  }, []);

  useEffect(() => {
    loadTransactions();
    loadAllTransactionsForSummary();
  }, [filters]);

  const loadAccounts = async () => {
    try {
      const isAdmin = user.role === 'ADMIN' || user.role === 'SUPPORT';
      
      if (isAdmin) {
        // For admin: get all accounts separately
        const [allAccountsRes, userAccountsRes] = await Promise.all([
          apiService.getAllAccounts().catch(() => ({ accounts: [] })),
          apiService.getUserAccounts(user.id).catch(() => ({ accounts: [] }))
        ]);
        
        setAllAccounts(allAccountsRes.accounts || []);
        setAccounts(userAccountsRes.accounts || []); // Only admin's own accounts for classification
      } else {
        // For regular users: get their own accounts
        const accountsRes = await apiService.getAccounts();
        setAccounts(accountsRes.accounts || []);
        setAllAccounts([]); // Regular users don't need all accounts
      }
    } catch (err) {
      console.error('Failed to load accounts:', err);
      // Don't show error for account loading since it's not critical
      // setError('Failed to load accounts: ' + err.message);
    }
  };

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const queryFilters = {};
      
      if (filters.accountId) queryFilters.accountId = filters.accountId;
      if (filters.status) queryFilters.status = filters.status;
      
      if (filters.dateFrom) queryFilters.startDate = filters.dateFrom;
      if (filters.dateTo) queryFilters.endDate = filters.dateTo;
      
      // If type, fee filter, or search is active, load all data for client-side filtering and pagination
      if (filters.type || filters.feeFilter || filters.searchQuery) {
        // Load all transactions for client-side filtering
        let allTransactions = [];
        let offset = 0;
        const limit = 100; // Maximum allowed by backend
        let hasMore = true;
        
        while (hasMore) {
          const batchFilters = { ...queryFilters, limit, offset };
          const response = await apiService.getTransactions(batchFilters);
          const batchTransactions = response.transactions || [];
          
          allTransactions = [...allTransactions, ...batchTransactions];
          hasMore = batchTransactions.length === limit;
          offset += limit;
        }
        
        // Apply client-side filtering
        let filteredTransactions = allTransactions;
        
        // Apply type filtering if active
        if (filters.type) {
          filteredTransactions = filteredTransactions.filter(transaction => {
            const smartType = getSmartTransactionType(transaction);
            const originalType = transaction.transaction_type;
            
            switch (filters.type) {
              case 'INTERNAL_TRANSFER':
                return smartType === 'INTERNAL_TRANSFER';
              case 'THIRD_PARTY_TRANSFER':
                return smartType === 'THIRD_PARTY_TRANSFER';
              case 'EXTERNAL_INCOMING':
                return smartType === 'EXTERNAL_TRANSFER_IN' || originalType === 'CREDIT';
              case 'EXTERNAL_OUTGOING':
                return smartType === 'EXTERNAL_TRANSFER_OUT' || originalType === 'DEBIT';
              case 'TRANSFER':
                return originalType === 'TRANSFER';
              default:
                return smartType === filters.type || originalType === filters.type;
            }
          });
        }
        
        // Apply fee filtering if active
        if (filters.feeFilter) {
          filteredTransactions = filteredTransactions.filter(transaction => {
            const feeAmount = parseFloat(transaction.fee_amount) || 0;
            const smartType = getSmartTransactionType(transaction);
            
            switch (filters.feeFilter) {
              case 'WITH_FEE':
                return feeAmount > 0;
              case 'NO_FEE':
                return feeAmount === 0;
              case 'INTERNAL_ONLY':
                return smartType === 'INTERNAL_TRANSFER';
              case 'EXTERNAL_ONLY':
                return smartType === 'EXTERNAL_TRANSFER_OUT' || smartType === 'EXTERNAL_TRANSFER_IN' || smartType === 'THIRD_PARTY_TRANSFER';
              default:
                return true;
            }
          });
        }
        
        // Apply search filtering if active
        if (filters.searchQuery && filters.searchQuery.trim()) {
          const searchTerm = filters.searchQuery.toLowerCase().trim();
          filteredTransactions = filteredTransactions.filter(transaction => {
            return (
              // Search in description
              (transaction.description || '').toLowerCase().includes(searchTerm) ||
              // Search in reference number
              (transaction.reference_number || '').toLowerCase().includes(searchTerm) ||
              // Search in account numbers
              (transaction.from_account_number || '').toLowerCase().includes(searchTerm) ||
              (transaction.to_account_number || '').toLowerCase().includes(searchTerm) ||
              // Search in user names
              (`${transaction.from_user_first_name || ''} ${transaction.from_user_last_name || ''}`).toLowerCase().includes(searchTerm) ||
              (`${transaction.to_user_first_name || ''} ${transaction.to_user_last_name || ''}`).toLowerCase().includes(searchTerm) ||
              // Search in amount
              transaction.amount.toString().includes(searchTerm)
            );
          });
        }
        
        // Client-side pagination
        const currentPage = filters.page || 1;
        const pageLimit = filters.limit || 10;
        const startIndex = (currentPage - 1) * pageLimit;
        const endIndex = startIndex + pageLimit;
        const paginatedTransactions = filteredTransactions.slice(startIndex, endIndex);
        
        setTransactions(paginatedTransactions);
        setPagination({
          currentPage: currentPage,
          totalPages: Math.max(1, Math.ceil(filteredTransactions.length / pageLimit)),
          totalTransactions: filteredTransactions.length,
          limit: pageLimit
        });
        
      } else {
        // Normal server-side pagination when no type filter
        if (filters.page > 1) {
          queryFilters.offset = (filters.page - 1) * filters.limit;
        }
        queryFilters.limit = filters.limit;
        
        console.log('🔍 Sending filters to backend:', queryFilters);
        
        const response = await apiService.getTransactions(queryFilters);
        const transactions = response.transactions || [];
        
        setTransactions(transactions);
        setPagination({
          currentPage: response.pagination?.currentPage || 1,
          totalPages: response.pagination?.totalPages || 1,
          totalTransactions: response.pagination?.total || 0,
          limit: response.pagination?.limit || 10
        });
      }
    } catch (err) {
      setError('Failed to load transactions: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAllTransactionsForSummary = async () => {
    try {
      const summaryFilters = {};
      
      if (filters.accountId) summaryFilters.accountId = filters.accountId;
      if (filters.status) summaryFilters.status = filters.status;
      
      if (filters.dateFrom) summaryFilters.startDate = filters.dateFrom;
      if (filters.dateTo) summaryFilters.endDate = filters.dateTo;
      
      let allTransactions = [];
      let offset = 0;
      const limit = 100; // Maximum allowed by backend
      let hasMore = true;
      
      console.log('📊 Loading all transactions for smart summary...');
      
      while (hasMore) {
        const batchFilters = { ...summaryFilters, limit, offset };
        const response = await apiService.getTransactions(batchFilters);
        const batchTransactions = response.transactions || [];
        
        allTransactions = [...allTransactions, ...batchTransactions];
        
        // Check if we got less than limit, meaning no more data
        hasMore = batchTransactions.length === limit;
        offset += limit;
        
        console.log(`📊 Loaded batch: ${batchTransactions.length} transactions, total: ${allTransactions.length}`);
      }
      
      // Client-side filtering for summary
      if (filters.type) {
        allTransactions = allTransactions.filter(transaction => {
          const smartType = getSmartTransactionType(transaction);
          const originalType = transaction.transaction_type;
          
          switch (filters.type) {
            case 'INTERNAL_TRANSFER':
              return smartType === 'INTERNAL_TRANSFER';
            case 'THIRD_PARTY_TRANSFER':
              return smartType === 'THIRD_PARTY_TRANSFER';
            case 'EXTERNAL_INCOMING':
              return smartType === 'EXTERNAL_TRANSFER_IN' || originalType === 'CREDIT';
            case 'EXTERNAL_OUTGOING':
              return smartType === 'EXTERNAL_TRANSFER_OUT' || originalType === 'DEBIT';
            case 'TRANSFER':
              return originalType === 'TRANSFER';
            default:
              return smartType === filters.type || originalType === filters.type;
          }
        });
      }
      
      // Apply fee filtering for summary if active
      if (filters.feeFilter) {
        allTransactions = allTransactions.filter(transaction => {
          const feeAmount = parseFloat(transaction.fee_amount) || 0;
          const smartType = getSmartTransactionType(transaction);
          
          switch (filters.feeFilter) {
            case 'WITH_FEE':
              return feeAmount > 0;
            case 'NO_FEE':
              return feeAmount === 0;
            case 'INTERNAL_ONLY':
              return smartType === 'INTERNAL_TRANSFER';
            case 'EXTERNAL_ONLY':
              return smartType === 'EXTERNAL_TRANSFER_OUT' || smartType === 'EXTERNAL_TRANSFER_IN' || smartType === 'THIRD_PARTY_TRANSFER';
            default:
              return true;
          }
        });
      }
      
      // Apply search filtering for summary if active
      if (filters.searchQuery && filters.searchQuery.trim()) {
        const searchTerm = filters.searchQuery.toLowerCase().trim();
        allTransactions = allTransactions.filter(transaction => {
          return (
            // Search in description
            (transaction.description || '').toLowerCase().includes(searchTerm) ||
            // Search in reference number
            (transaction.reference_number || '').toLowerCase().includes(searchTerm) ||
            // Search in account numbers
            (transaction.from_account_number || '').toLowerCase().includes(searchTerm) ||
            (transaction.to_account_number || '').toLowerCase().includes(searchTerm) ||
            // Search in user names
            (`${transaction.from_user_first_name || ''} ${transaction.from_user_last_name || ''}`).toLowerCase().includes(searchTerm) ||
            (`${transaction.to_user_first_name || ''} ${transaction.to_user_last_name || ''}`).toLowerCase().includes(searchTerm) ||
            // Search in amount
            transaction.amount.toString().includes(searchTerm)
          );
        });
      }
      
      console.log(`📊 Summary complete: ${allTransactions.length} total transactions (after type filter)`);
      setAllTransactionsForSummary(allTransactions);
    } catch (err) {
      console.error('Failed to load transactions for summary:', err);
      setAllTransactionsForSummary([]);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  const clearFilters = () => {
    setFilters({
      accountId: '',
      status: '',
      type: '',
      feeFilter: '',
      dateFrom: '',
      dateTo: '',
      searchQuery: '',
      page: 1,
      limit: 10  // ✅ Установили 10 записей на страницу
    });
  };

  const changePage = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  const handleExport = async () => {
    try {
      console.log('📄 Starting export with current filters:', filters);
      
      // Get all transactions that match current filters (including type filter)
      const exportFilters = {};
      
      if (filters.accountId) exportFilters.accountId = filters.accountId;
      if (filters.status) exportFilters.status = filters.status;
      if (filters.dateFrom) exportFilters.startDate = filters.dateFrom;
      if (filters.dateTo) exportFilters.endDate = filters.dateTo;
      
      // Load all matching transactions (same logic as summary)
      let allTransactions = [];
      let offset = 0;
      const limit = 100;
      let hasMore = true;
      
      console.log('📄 Loading all transactions for export...');
      
      while (hasMore) {
        const batchFilters = { ...exportFilters, limit, offset };
        const response = await apiService.getTransactions(batchFilters);
        const batchTransactions = response.transactions || [];
        
        allTransactions = [...allTransactions, ...batchTransactions];
        hasMore = batchTransactions.length === limit;
        offset += limit;
      }
      
      console.log(`📄 Loaded ${allTransactions.length} transactions before type filtering`);
      
      // Apply client-side type filtering if active
      if (filters.type) {
        allTransactions = allTransactions.filter(transaction => {
          const smartType = getSmartTransactionType(transaction);
          const originalType = transaction.transaction_type;
          
          switch (filters.type) {
            case 'INTERNAL_TRANSFER':
              return smartType === 'INTERNAL_TRANSFER';
            case 'THIRD_PARTY_TRANSFER':
              return smartType === 'THIRD_PARTY_TRANSFER';
            case 'EXTERNAL_INCOMING':
              return smartType === 'EXTERNAL_TRANSFER_IN' || originalType === 'CREDIT';
            case 'EXTERNAL_OUTGOING':
              return smartType === 'EXTERNAL_TRANSFER_OUT' || originalType === 'DEBIT';
            case 'TRANSFER':
              return originalType === 'TRANSFER';
            default:
              return smartType === filters.type || originalType === filters.type;
          }
        });
      }
      
      // Apply search filtering for export if active
      if (filters.searchQuery && filters.searchQuery.trim()) {
        const searchTerm = filters.searchQuery.toLowerCase().trim();
        allTransactions = allTransactions.filter(transaction => {
          return (
            // Search in description
            (transaction.description || '').toLowerCase().includes(searchTerm) ||
            // Search in reference number
            (transaction.reference_number || '').toLowerCase().includes(searchTerm) ||
            // Search in account numbers
            (transaction.from_account_number || '').toLowerCase().includes(searchTerm) ||
            (transaction.to_account_number || '').toLowerCase().includes(searchTerm) ||
            // Search in user names
            (`${transaction.from_user_first_name || ''} ${transaction.from_user_last_name || ''}`).toLowerCase().includes(searchTerm) ||
            (`${transaction.to_user_first_name || ''} ${transaction.to_user_last_name || ''}`).toLowerCase().includes(searchTerm) ||
            // Search in amount
            transaction.amount.toString().includes(searchTerm)
          );
        });
      }
      
      console.log(`📄 Export ready: ${allTransactions.length} transactions after filtering`);
      
      // Add smart type classification to export data
      const exportData = {
        metadata: {
          exportDate: new Date().toISOString(),
          filters: filters,
          totalTransactions: allTransactions.length,
          tenant: user?.tenant || 'Unknown'
        },
        transactions: allTransactions.map(transaction => ({
          ...transaction,
          smartType: getSmartTransactionType(transaction),
          smartAmount: getSmartAmount(transaction)
        }))
      };
      
      // Create and download JSON file
      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `transactions_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      console.log('📄 Export completed successfully');
    } catch (err) {
      console.error('📄 Export failed:', err);
      setError('Export failed: ' + err.message);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    return (
      <div style={{ lineHeight: '1.3' }}>
        <div>{day}.{month}.{year}</div>
        <div>{hours}:{minutes}</div>
      </div>
    );
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(Math.abs(amount));
  };

  const getAccountLabel = (accountId) => {
    const account = accounts.find(acc => acc.id === accountId) || 
                   allAccounts.find(acc => acc.id === accountId);
    return account ? `${account.account_number} (${account.account_type})` : 'Unknown Account';
  };

  // Smart Transfer Logic
  const isInternalTransfer = (transaction) => {
    if (transaction.transaction_type !== 'TRANSFER') return false;
    
    // Check if both accounts belong to the same user
    const fromUserName = `${transaction.from_user_first_name || ''} ${transaction.from_user_last_name || ''}`.trim();
    const toUserName = `${transaction.to_user_first_name || ''} ${transaction.to_user_last_name || ''}`.trim();
    
    // If either user name is empty (account deleted), consider it as different users
    if (!fromUserName || !toUserName) return false;
    
    return fromUserName === toUserName;
  };

  const getSmartTransactionType = (transaction) => {
    if (transaction.transaction_type === 'TRANSFER') {
      if (isInternalTransfer(transaction)) {
        return 'INTERNAL_TRANSFER';
      } else {
        // For external transfers, check account ownership through our accounts list
        const currentUserAccounts = accounts.map(acc => acc.account_number);
        const fromAccount = transaction.from_account_number;
        const toAccount = transaction.to_account_number;
        
        console.log(`🔍 Transfer Direction Check:`, {
          currentUserAccounts,
          fromAccount,
          toAccount,
          userRole: user?.role,
          fromUser: `${transaction.from_user_first_name || 'DELETED'} ${transaction.from_user_last_name || ''}`.trim(),
          toUser: `${transaction.to_user_first_name || 'DELETED'} ${transaction.to_user_last_name || ''}`.trim(),
          isFromAccountMine: currentUserAccounts.includes(fromAccount),
          isToAccountMine: currentUserAccounts.includes(toAccount)
        });
        
        // For admins viewing third-party transfers, show neutral classification
        if (user && ['ADMIN', 'SUPPORT'].includes(user.role)) {
          if (!currentUserAccounts.includes(fromAccount) && !currentUserAccounts.includes(toAccount)) {
            console.log('👨‍💼 ADMIN VIEW: Third-party transfer between other users');
            return 'THIRD_PARTY_TRANSFER';
          }
        }
        
        // If current user owns the FROM account, it's outgoing
        if (currentUserAccounts.includes(fromAccount)) {
          console.log('→ OUTGOING: Current user owns FROM account');
          return 'EXTERNAL_TRANSFER_OUT'; // Outgoing
        }
        // If current user owns the TO account, it's incoming  
        else if (currentUserAccounts.includes(toAccount)) {
          console.log('← INCOMING: Current user owns TO account');
          return 'EXTERNAL_TRANSFER_IN'; // Incoming
        }
        // Fallback (shouldn't happen if data is correct)
        else {
          console.log('⚠️ FALLBACK: Neither FROM nor TO account belongs to current user');
          return 'EXTERNAL_TRANSFER_OUT'; 
        }
      }
    }
    // For CREDIT/DEBIT, return original type
    return transaction.transaction_type;
  };

  const getSmartAmount = (transaction) => {
    const smartType = getSmartTransactionType(transaction);
    
    if (smartType === 'INTERNAL_TRANSFER') {
      // No +/- for internal transfers
      return formatAmount(transaction.amount);
    } else if (smartType === 'THIRD_PARTY_TRANSFER') {
      // No +/- for third-party transfers (admin view)
      return formatAmount(transaction.amount);
    } else if (smartType === 'EXTERNAL_TRANSFER_OUT') {
      // Outgoing external transfer
      return '- ' + formatAmount(transaction.amount);
    } else if (smartType === 'EXTERNAL_TRANSFER_IN') {
      // Incoming external transfer
      return '+ ' + formatAmount(transaction.amount);
    } else if (transaction.transaction_type === 'CREDIT') {
      return '+ ' + formatAmount(transaction.amount);
    } else if (transaction.transaction_type === 'DEBIT') {
      return '- ' + formatAmount(transaction.amount);
    } else {
      // For other types, use original logic
      return (transaction.transaction_type === 'CREDIT' ? '+' : '-') + ' ' + formatAmount(transaction.amount);
    }
  };

  // Smart Summary Calculation
  const calculateSmartSummary = (transactions) => {
    let totalTransactions = transactions.length;
    let externalCredits = 0;
    let externalDebits = 0;
    let internalTransfers = 0;
    let thirdPartyTransfers = 0;
    
    transactions.forEach(transaction => {
      const smartType = getSmartTransactionType(transaction);
      const amount = parseFloat(transaction.amount);
      
      if (smartType === 'INTERNAL_TRANSFER') {
        internalTransfers += amount;
      } else if (smartType === 'THIRD_PARTY_TRANSFER') {
        thirdPartyTransfers += amount;
      } else if (smartType === 'EXTERNAL_TRANSFER_OUT') {
        externalDebits += amount;
      } else if (smartType === 'EXTERNAL_TRANSFER_IN') {
        externalCredits += amount;
      } else if (transaction.transaction_type === 'CREDIT') {
        externalCredits += amount;
      } else {
        externalDebits += amount;
      }
    });
    
    const netExternalFlow = externalCredits - externalDebits;
    
    return {
      totalTransactions,
      externalCredits,
      externalDebits,
      internalTransfers,
      thirdPartyTransfers,
      netExternalFlow
    };
  };

  return (
    <PageContainer data-testid="transaction-history-page">
      <PageTitle $isDarkMode={isDarkMode}>
        <span>📊 Transaction History</span>
      </PageTitle>

      {error && <ErrorMessage $isDarkMode={isDarkMode}>{error}</ErrorMessage>}

      {/* Summary Section */}
      {allTransactionsForSummary.length > 0 && (
        <Section $isDarkMode={isDarkMode}>
          <SectionTitle $isDarkMode={isDarkMode}>Smart Summary (All Filtered Data)</SectionTitle>
          <SummaryGrid>
            <SummaryCard $isDarkMode={isDarkMode} color="#e3f2fd" darkColor="#1e2a3a">
              <SummaryTitle $isDarkMode={isDarkMode}>Total Transactions</SummaryTitle>
              <SummaryValue $isDarkMode={isDarkMode}>{calculateSmartSummary(allTransactionsForSummary).totalTransactions}</SummaryValue>
            </SummaryCard>
            <SummaryCard $isDarkMode={isDarkMode} color="#e8f5e8" darkColor="#1e3a1e">
              <SummaryTitle $isDarkMode={isDarkMode}>External Credits</SummaryTitle>
              <SummaryValue $isDarkMode={isDarkMode}>{formatAmount(calculateSmartSummary(allTransactionsForSummary).externalCredits)}</SummaryValue>
            </SummaryCard>
            <SummaryCard $isDarkMode={isDarkMode} color="#fff3e0" darkColor="#3a3020">
              <SummaryTitle $isDarkMode={isDarkMode}>External Debits</SummaryTitle>
              <SummaryValue $isDarkMode={isDarkMode}>{formatAmount(calculateSmartSummary(allTransactionsForSummary).externalDebits)}</SummaryValue>
            </SummaryCard>
            <SummaryCard $isDarkMode={isDarkMode} color="#e1f5fe" darkColor="#1e2a3a">
              <SummaryTitle $isDarkMode={isDarkMode}>Internal Transfers</SummaryTitle>
              <SummaryValue $isDarkMode={isDarkMode}>{formatAmount(calculateSmartSummary(allTransactionsForSummary).internalTransfers)}</SummaryValue>
            </SummaryCard>
            {user && ['ADMIN', 'SUPPORT'].includes(user.role) && calculateSmartSummary(allTransactionsForSummary).thirdPartyTransfers > 0 && (
              <SummaryCard $isDarkMode={isDarkMode} color="#f0f0f0" darkColor="#2d2d2d">
                <SummaryTitle $isDarkMode={isDarkMode}>External Transfers</SummaryTitle>
                <SummaryValue $isDarkMode={isDarkMode}>{formatAmount(calculateSmartSummary(allTransactionsForSummary).thirdPartyTransfers)}</SummaryValue>
              </SummaryCard>
            )}
            <SummaryCard $isDarkMode={isDarkMode} color="#f3e5f5" darkColor="#3a1e3a">
              <SummaryTitle $isDarkMode={isDarkMode}>Net External Flow</SummaryTitle>
              <SummaryValue $isDarkMode={isDarkMode} style={{ 
                color: calculateSmartSummary(allTransactionsForSummary).netExternalFlow >= 0 ? '#28a745' : '#dc3545' 
              }}>
                {calculateSmartSummary(allTransactionsForSummary).netExternalFlow >= 0 ? '+' : ''}
                {formatAmount(calculateSmartSummary(allTransactionsForSummary).netExternalFlow)}
              </SummaryValue>
            </SummaryCard>
          </SummaryGrid>
        </Section>
      )}

      {/* Filters Section */}
      <Section $isDarkMode={isDarkMode}>
        <SectionTitle $isDarkMode={isDarkMode}>
          Filters
          <ButtonGroup>
            <Button 
              $isDarkMode={isDarkMode}
              variant="secondary" 
              onClick={clearFilters}
              data-testid="clear-filters"
            >
              Clear Filters
            </Button>
            <Button 
              $isDarkMode={isDarkMode}
              onClick={handleExport}
              data-testid="export-transactions"
            >
              Export CSV
            </Button>
          </ButtonGroup>
        </SectionTitle>
        
        <FilterGrid>
          <FormGroup>
            <Label $isDarkMode={isDarkMode}>Account</Label>
            <Select
              $isDarkMode={isDarkMode}
              data-testid="account-filter"
              value={filters.accountId}
              onChange={(e) => handleFilterChange('accountId', e.target.value)}
            >
              <option value="">All Accounts</option>
              {(user.role === 'ADMIN' || user.role === 'SUPPORT' ? allAccounts : accounts).map(account => (
                <option key={account.id} value={account.id}>
                  {account.account_number} ({account.account_type})
                  {account.user_id !== user.id && (user.role === 'ADMIN' || user.role === 'SUPPORT') && 
                    ` [${account.first_name} ${account.last_name}]`}
                </option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label $isDarkMode={isDarkMode}>Status</Label>
            <Select
              $isDarkMode={isDarkMode}
              data-testid="status-filter"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="COMPLETED">Completed</option>
              <option value="PENDING">Pending</option>
              <option value="FAILED">Failed</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label $isDarkMode={isDarkMode}>Type</Label>
            <Select
              $isDarkMode={isDarkMode}
              data-testid="type-filter"
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
            >
              <option value="">All Types</option>
              <option value="INTERNAL_TRANSFER">Internal Transfer</option>
              <option value="EXTERNAL_INCOMING">External Incoming</option>
              <option value="EXTERNAL_OUTGOING">External Outgoing</option>
              {user && ['ADMIN', 'SUPPORT'].includes(user.role) && (
                <option value="THIRD_PARTY_TRANSFER">External Transfer</option>
              )}
              <option value="TRANSFER">All Transfers</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label $isDarkMode={isDarkMode}>Fee Filter</Label>
            <Select
              $isDarkMode={isDarkMode}
              value={filters.feeFilter}
              onChange={(e) => handleFilterChange('feeFilter', e.target.value)}
            >
              <option value="">All Fees</option>
              <option value="WITH_FEE">With Fee</option>
              <option value="NO_FEE">No Fee</option>
              <option value="INTERNAL_ONLY">Internal Only (No Fees)</option>
              <option value="EXTERNAL_ONLY">External Only (May have Fees)</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label $isDarkMode={isDarkMode}>Date From</Label>
            <Input
              $isDarkMode={isDarkMode}
              type="date"
              data-testid="date-from-filter"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
            />
          </FormGroup>

          <FormGroup>
            <Label $isDarkMode={isDarkMode}>Date To</Label>
            <Input
              $isDarkMode={isDarkMode}
              type="date"
              data-testid="date-to-filter"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
            />
          </FormGroup>

          <FormGroup>
            <Label $isDarkMode={isDarkMode}>Search</Label>
            <Input
              $isDarkMode={isDarkMode}
              type="text"
              placeholder="Search by description, reference, or account..."
              value={filters.searchQuery}
              onChange={(e) => handleFilterChange('searchQuery', e.target.value)}
            />
          </FormGroup>
        </FilterGrid>
      </Section>

      {/* Transactions Table */}
      <Section $isDarkMode={isDarkMode}>
        <SectionTitle $isDarkMode={isDarkMode}>
          Transactions
          {loading && <LoadingSpinner $isDarkMode={isDarkMode} />}
        </SectionTitle>

        {transactions.length === 0 ? (
          <EmptyState $isDarkMode={isDarkMode}>
            {loading ? 'Loading transactions...' : 'No transactions found'}
          </EmptyState>
        ) : (
          <>
            <TransactionTable data-testid="transaction-table">
              <Table>
                <thead>
                  <tr>
                    <Th $isDarkMode={isDarkMode}>Date</Th>
                    <Th $isDarkMode={isDarkMode}>Account</Th>
                    <Th $isDarkMode={isDarkMode}>Type</Th>
                    <Th $isDarkMode={isDarkMode}>Amount</Th>
                    <Th $isDarkMode={isDarkMode}>Status</Th>
                    <Th $isDarkMode={isDarkMode}>Description</Th>
                    <Th $isDarkMode={isDarkMode}>Reference</Th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map(transaction => (
                    <Fragment key={transaction.id} data-testid={`transaction-${transaction.id}`}>
                      <tr>
                        <Td $isDarkMode={isDarkMode}>{formatDate(transaction.created_at)}</Td>
                        <Td $isDarkMode={isDarkMode}>
                          {transaction.transaction_type === 'TRANSFER' ? (
                            <div style={{ fontSize: '13px', lineHeight: '1.3' }}>
                              <div>↗️ {transaction.from_account_number ? `***${transaction.from_account_number.slice(-4)}` : '🗑️ DELETED'}</div>
                              <div>↘️ {transaction.to_account_number ? `***${transaction.to_account_number.slice(-4)}` : '🗑️ DELETED'}</div>
                            </div>
                          ) : (
                            <div style={{ fontSize: '13px' }}>
                              {transaction.from_account_number ? `***${transaction.from_account_number.slice(-4)}` :
                               transaction.to_account_number ? `***${transaction.to_account_number.slice(-4)}` : '🗑️ DELETED'}
                            </div>
                          )}
                        </Td>
                        <Td $isDarkMode={isDarkMode}>
                          <TypeBadge type={getSmartTransactionType(transaction)}>
                            {getSmartTransactionType(transaction) === 'INTERNAL_TRANSFER' ? 'INTERNAL' :
                             getSmartTransactionType(transaction) === 'THIRD_PARTY_TRANSFER' ? 'EXTERNAL' :
                             getSmartTransactionType(transaction) === 'EXTERNAL_TRANSFER_OUT' ? 'OUTGOING' :
                             getSmartTransactionType(transaction) === 'EXTERNAL_TRANSFER_IN' ? 'INCOMING' :
                             getSmartTransactionType(transaction)}
                          </TypeBadge>
                        </Td>
                        <Td $isDarkMode={isDarkMode} className="amount">
                          {getSmartAmount(transaction)}
                        </Td>
                        <Td $isDarkMode={isDarkMode} className="status">
                          <StatusBadge status={transaction.status}>
                            {transaction.status}
                          </StatusBadge>
                        </Td>
                        <Td $isDarkMode={isDarkMode}>{transaction.description}</Td>
                        <Td $isDarkMode={isDarkMode}>{transaction.reference_number}</Td>
                      </tr>
                                              {/* Fee row - только для исходящих переводов */}
                        {transaction.fee_amount && parseFloat(transaction.fee_amount) > 0 && 
                         getSmartTransactionType(transaction) !== 'EXTERNAL_TRANSFER_IN' && (
                        <tr style={{ 
                          background: isDarkMode ? '#3a3020' : '#fff3cd', 
                          fontSize: '13px',
                          color: isDarkMode ? '#ffffff' : '#333'
                        }}>
                          <Td $isDarkMode={isDarkMode}></Td>
                          <Td $isDarkMode={isDarkMode} style={{ 
                            color: isDarkMode ? '#ffd43b' : '#856404', 
                            fontStyle: 'italic' 
                          }}>Transfer Fee</Td>
                          <Td $isDarkMode={isDarkMode}></Td>
                          <Td $isDarkMode={isDarkMode} className="amount" style={{ color: '#dc3545', fontWeight: '600' }}>
                            -{formatAmount(parseFloat(transaction.fee_amount))}
                          </Td>
                          <Td $isDarkMode={isDarkMode}></Td>
                          <Td $isDarkMode={isDarkMode} style={{ 
                            color: isDarkMode ? '#ffd43b' : '#856404', 
                            fontStyle: 'italic' 
                          }}>External transfer fee</Td>
                          <Td $isDarkMode={isDarkMode}></Td>
                        </tr>
                      )}
                                         </Fragment>
                  ))}
                </tbody>
              </Table>
            </TransactionTable>

            {/* Modern Pagination */}
            {pagination.totalPages > 1 && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '1rem',
                marginTop: '2rem',
                paddingTop: '2rem',
                borderTop: isDarkMode ? '1px solid #404040' : '1px solid #dee2e6'
              }}>
                <button
                  onClick={() => changePage(Math.max(1, pagination.currentPage - 1))}
                  disabled={pagination.currentPage <= 1}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: pagination.currentPage <= 1 
                      ? (isDarkMode ? '#333' : '#f5f5f5') 
                      : (isDarkMode ? '#4dabf7' : '#007bff'),
                    color: pagination.currentPage <= 1 
                      ? (isDarkMode ? '#666' : '#999') 
                      : 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: pagination.currentPage <= 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  Previous
                </button>
                
                <span style={{ color: isDarkMode ? '#b3b3b3' : '#666' }}>
                  Page {pagination.currentPage} of {pagination.totalPages}
                </span>
                
                <button
                  onClick={() => changePage(Math.min(pagination.totalPages, pagination.currentPage + 1))}
                  disabled={pagination.currentPage >= pagination.totalPages}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: pagination.currentPage >= pagination.totalPages 
                      ? (isDarkMode ? '#333' : '#f5f5f5') 
                      : (isDarkMode ? '#4dabf7' : '#007bff'),
                    color: pagination.currentPage >= pagination.totalPages 
                      ? (isDarkMode ? '#666' : '#999') 
                      : 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: pagination.currentPage >= pagination.totalPages ? 'not-allowed' : 'pointer'
                  }}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </Section>
    </PageContainer>
  );
}

export default TransactionHistoryPage; 